import React, { useCallback, useEffect, useMemo, useRef, useState, useId } from 'react'
import type { Dispatch, SetStateAction, SyntheticEvent } from 'react'

import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import ErrorIcon from '@mui/icons-material/Error'
import { Autocomplete, CircularProgress, Grid, TextField } from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import clsx from 'clsx'
import { useCenterSupplier } from 'hooks/useCenterSupplier'
import { useSupplier } from 'hooks/useSupplier'
import _ from 'lodash'
import { SelectFormControl } from 'style'
import 'utils/mst.scss'

import SupplierDetail from '../SupplierDetail/SupplierDetail'

import type { MstCenterSupplier, MstSupplier } from 'api-client'
import type { AutocompleteSupplier } from 'hooks/useSupplier'

interface SelectSupplierProps {
  trCompanyId: number | undefined
  orgSupplierId: string | null | undefined
  orgCenterId?: string | null | undefined
  centerId?: number | null | undefined
  centerIdList?: number[]
  orgCenterIdList?: string[]
  setOrgSupplierId: Dispatch<SetStateAction<string | null | undefined>>
  setSupplierInfo: Dispatch<SetStateAction<MstSupplier | undefined>>
  setCenterSupplierInfo?: Dispatch<SetStateAction<MstCenterSupplier | undefined>>
  isRequred: boolean
  setIsValid: Dispatch<SetStateAction<boolean>>
  enabled?: boolean
  alignmentFlag?: boolean
  isMultiple?: boolean
  hideInfoMark?: boolean  // Hide supplier detail info icon
  orgSupplierIdList?: string[]
  setOrgSupplierIdList?: Dispatch<SetStateAction<string[]>>
  setSupplierInfoList?: Dispatch<SetStateAction<MstSupplier[]>>
  setCenterSupplierInfoList?: Dispatch<SetStateAction<MstCenterSupplier[]>>
  startSelectTop?: boolean
  /** Multiple: dropdown opened — passes current selected orgSupplierIds from grid state. */
  onMultipleDropdownOpen?: (orgSupplierIds: string[]) => void
  /** Multiple: dropdown closed — passes ids at close time (avoids stale parent state). */
  onMultipleDropdownClose?: (orgSupplierIds: string[]) => void
  /** Multiple: chip X or clear-all removed (change outside dropdown session). */
  onMultipleChipRemoved?: () => void
}

function SelectSupplier({
  trCompanyId,
  orgSupplierId,
  orgCenterId,
  centerId,
  centerIdList,
  orgCenterIdList,
  setOrgSupplierId,
  setSupplierInfo,
  setCenterSupplierInfo,
  isRequred,
  setIsValid,
  enabled,
  alignmentFlag = false,
  isMultiple,
  hideInfoMark = false,
  orgSupplierIdList: orgSupplierIdListProp,
  setOrgSupplierIdList,
  setSupplierInfoList,
  setCenterSupplierInfoList,
  startSelectTop,
  onMultipleDropdownOpen,
  onMultipleDropdownClose,
  onMultipleChipRemoved,
}: SelectSupplierProps) {
  const { loginUserInfo } = useAuthContext()
  const uniqueId = useId()
  const { supplierSearch, autocompletSuppliers, fetchSupplierSearch, supplierSearchIsLoading } = useSupplier()
  const { fetchCenterSupplierSearch, centerSupplierSearch } = useCenterSupplier()
  const [trValue, setTrValue] = useState<AutocompleteSupplier | null | undefined>(undefined)
  const [trInputValue, setTrInputValue] = useState<string>('')
  const [trValueList, setTrValueList] = useState<AutocompleteSupplier[]>([])
  /** Latest multi selection; updated synchronously in onChange for accurate onClose ids. */
  const latestTrValueListRef = useRef<AutocompleteSupplier[]>([])
  const isFirstRenderRef = useRef(true)
  const prevTrCompanyIdRef = useRef<number | undefined>(undefined)
  const [open, setOpen] = useState(false)

  // Create Maps for faster lookup
  const supplierMapByOrgId = useMemo(() => {
    const map = new Map<string, MstSupplier>()
    supplierSearch?.list.forEach((supplier) => {
      if (supplier.orgSupplierId != null) {
        map.set(supplier.orgSupplierId, supplier)
      }
    })
    return map
  }, [supplierSearch?.list])

  const centerSupplierMapByOrgId = useMemo(() => {
    const map = new Map<string, MstCenterSupplier[]>()
    centerSupplierSearch?.list.forEach((centerSupplier) => {
      if (centerSupplier.orgSupplierId != null) {
        const existing = map.get(centerSupplier.orgSupplierId) || []
        map.set(centerSupplier.orgSupplierId, [...existing, centerSupplier])
      }
    })
    return map
  }, [centerSupplierSearch?.list])

  const findCenterSupplier = (supplierId: string | null | undefined): MstCenterSupplier | undefined => {
    if (!supplierId || !centerSupplierSearch?.list || (!centerId && !orgCenterId)) return undefined;

    if (centerId) {
      return centerSupplierSearch.list.find(
        item => item.orgSupplierId === supplierId && item.centerId === centerId
      );
    }

    if (orgCenterId) {
      return centerSupplierSearch.list.find(
        item => item.orgSupplierId === supplierId && item.orgCenterId === orgCenterId
      );
    }

    return undefined;
  };

  const findCenterSuppliers = (supplierId: string | null | undefined): MstCenterSupplier[] => {
    if (!supplierId || !centerSupplierSearch?.list) return [];

    if (centerId) {
      return centerSupplierSearch.list.filter(
        item => item.orgSupplierId === supplierId && item.centerId === centerId
      );
    }

    if (orgCenterId) {
      return centerSupplierSearch.list.filter(
        item => item.orgSupplierId === supplierId && item.orgCenterId === orgCenterId
      );
    }

    return centerSupplierSearch.list.filter(
      item => item.orgSupplierId === supplierId
    );
  };

  useEffect(() => {
    if (!isFirstRenderRef.current) {
      // Only clear orgSupplierId if trCompanyId actually changed (not just on re-render)
      const trCompanyIdChanged = prevTrCompanyIdRef.current !== trCompanyId

      if (trCompanyIdChanged) {
        setOrgSupplierId(undefined)
        setTrValue(undefined)

        // Clear multiple selection values
        if (isMultiple) {
          setTrValueList([])
          setOrgSupplierIdList?.([])
          setSupplierInfoList?.([])
          setCenterSupplierInfoList?.([])
        }
      }
    }

    prevTrCompanyIdRef.current = trCompanyId

    if (trCompanyId === undefined || trCompanyId === null) {
      fetchSupplierSearch({ enabled: enabled ?? undefined, companyId: loginUserInfo!.companyId }, false)
      fetchCenterSupplierSearch({ enabled: enabled ?? undefined, companyId: loginUserInfo!.companyId }, false)
    } else {
      fetchSupplierSearch({ enabled: enabled ?? undefined, companyId: trCompanyId }, false)
      fetchCenterSupplierSearch({ enabled: enabled ?? undefined, companyId: trCompanyId }, false)
    }
  }, [trCompanyId, enabled, isMultiple, setOrgSupplierId, setOrgSupplierIdList, setSupplierInfoList, setCenterSupplierInfoList, loginUserInfo, fetchSupplierSearch, fetchCenterSupplierSearch])

  useEffect(() => {
    // orgSupplierIdが空の場合はクリアにする
    if (!isMultiple) {
      if (orgSupplierId === undefined && trValue) {
        setTrValue(null)
        setTrInputValue('')
      }
    }
    // Multiple selection mode doesn't need this clear logic as it's handled differently
  }, [orgSupplierId, trValue, isMultiple])

  // 必須チェック処理
  useEffect(() => {
    // 必須チェックを上位コンポーネントに通知
    const isValid = !isMultiple ? !!trValue : trValueList.length > 0
    setIsValid(!isValid)
  }, [trValue, trValueList, isMultiple, setIsValid])

  useEffect(() => {
    // 仕入先APIが呼び出されレスポンスが返ってきた場合のみ実行
    if (supplierSearch?.list.length) {
      if (!isMultiple) {
        // Single selection mode
        if (autocompletSuppliers.length > 0 && orgSupplierId) {
          const matched = _.find(autocompletSuppliers, { orgSupplierId })
          setTrValue(matched)
          setTrInputValue(matched ? getOptionLabel(matched) : '')
        }
      }
      if (isMultiple) {
        // Multiple: sync from parent orgSupplierIdList (URL/localStorage) when we have options
        if (orgSupplierIdListProp?.length && autocompletSuppliers.length > 0) {
          const matched = orgSupplierIdListProp
            .map((id) => _.find(autocompletSuppliers, { orgSupplierId: id }))
            .filter((s): s is AutocompleteSupplier => s != null)
          setTrValueList(matched)
        } else if (isFirstRenderRef.current) {
          setTrValueList([])
        }
      }
    }

    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
    }
  }, [supplierSearch, autocompletSuppliers, isMultiple, orgSupplierId, orgSupplierIdListProp])

  // When parent clears orgSupplierIdList in multiple mode, also clear local selection list
  useEffect(() => {
    if (isMultiple && (!orgSupplierIdListProp || orgSupplierIdListProp.length === 0)) {
      setTrValueList([])
    }
  }, [isMultiple, orgSupplierIdListProp])

  useEffect(() => {
    if (!isMultiple && supplierSearch?.list && orgSupplierId) {
      setSupplierInfo(_.find(supplierSearch.list, { orgSupplierId }))
    }
  }, [supplierSearch, orgSupplierId, isMultiple, setSupplierInfo])

  useEffect(() => {
    if (!isMultiple && centerSupplierSearch?.list && orgSupplierId && (centerId || orgCenterId)) {
      setCenterSupplierInfo?.(findCenterSupplier(orgSupplierId))
    }
  }, [centerSupplierSearch, orgSupplierId, centerId, orgCenterId, isMultiple, setCenterSupplierInfo])

  // Multi mode: no cross-select filtering (show all).
  // Single mode: keep filtering by selected center (centerId/orgCenterId) when provided.
  const filteredSuppliers = useMemo(() => {
    if (isMultiple) return autocompletSuppliers
    if (!centerSupplierSearch) return autocompletSuppliers

    // Single center filtering (backward compatibility)
    if (centerId || orgCenterId) {
      const centerSuppliers = centerSupplierSearch.list.filter((item) => {
        if (centerId && item.centerId === centerId) return true
        if (orgCenterId && item.orgCenterId === orgCenterId) return true
        return false
      })
      const centerSupplierIds = centerSuppliers.map((item) => item.orgSupplierId)
      return autocompletSuppliers.filter((supplier) => centerSupplierIds.includes(supplier.orgSupplierId))
    }

    return autocompletSuppliers
  }, [autocompletSuppliers, centerSupplierSearch, centerId, orgCenterId, isMultiple])

  // Helper function to handle single selection
  const handleSingleSelection = useCallback((selectedValue: AutocompleteSupplier | null) => {
    if (selectedValue && selectedValue.orgSupplierId) {
      setTrValue(selectedValue)
      setTrInputValue(getOptionLabel(selectedValue))
      setOrgSupplierId(selectedValue.orgSupplierId)

      const supplierInfo = supplierMapByOrgId.get(selectedValue.orgSupplierId)
      if (supplierInfo) {
        setSupplierInfo(supplierInfo)
      }

      const centerSupplier = findCenterSupplier(selectedValue.orgSupplierId)
      if (centerSupplier) {
        setCenterSupplierInfo?.(centerSupplier)
      }

      // Always set multiple values for consistency
      const orgSupplierIds = [selectedValue.orgSupplierId]
      const supplierInfos = supplierInfo ? [supplierInfo] : []
      const centerSuppliers = centerSupplier ? [centerSupplier] : []

      setOrgSupplierIdList?.(orgSupplierIds)
      setSupplierInfoList?.(supplierInfos)
      setCenterSupplierInfoList?.(centerSuppliers)
    } else {
      setTrValue(null)
      setTrInputValue('')
      setOrgSupplierId(undefined)
      setSupplierInfo(undefined)
      setCenterSupplierInfo?.(undefined)
      setOrgSupplierIdList?.([])
      setSupplierInfoList?.([])
      setCenterSupplierInfoList?.([])
    }
  }, [setOrgSupplierId, setSupplierInfo, setCenterSupplierInfo, setOrgSupplierIdList, setSupplierInfoList, setCenterSupplierInfoList, supplierMapByOrgId, findCenterSupplier])

  // Helper function to handle multiple selection
  useEffect(() => {
    latestTrValueListRef.current = trValueList
  }, [trValueList])

  const handleMultipleSelection = useCallback((selectedValues: AutocompleteSupplier[]) => {
    latestTrValueListRef.current = selectedValues
    setTrValueList(selectedValues)

    const orgSupplierIds = selectedValues
      .map(v => v.orgSupplierId)
      .filter((id): id is string => id !== null && id !== undefined)
    const supplierInfos = selectedValues
      .map(v => v.orgSupplierId ? supplierMapByOrgId.get(v.orgSupplierId) : undefined)
      .filter((info): info is MstSupplier => info !== undefined)
    const centerSuppliers = selectedValues
      .flatMap(v => v.orgSupplierId ? findCenterSuppliers(v.orgSupplierId) : [])
      .filter((info): info is MstCenterSupplier => info !== undefined)

    setOrgSupplierIdList?.(orgSupplierIds)
    setSupplierInfoList?.(supplierInfos)
    setCenterSupplierInfoList?.(centerSuppliers)

    // Also set single values for backward compatibility
    if (selectedValues.length > 0) {
      const firstValue = selectedValues[0]
      const firstOrgSupplierId = firstValue.orgSupplierId
      if (firstOrgSupplierId) {
        setOrgSupplierId(firstOrgSupplierId)
        const supplierInfo = supplierMapByOrgId.get(firstOrgSupplierId)
        if (supplierInfo) {
          setSupplierInfo(supplierInfo)
        }
        const centerSupplier = findCenterSupplier(firstOrgSupplierId)
        if (centerSupplier) {
          setCenterSupplierInfo?.(centerSupplier)
        }
      } else {
        setOrgSupplierId(undefined)
        setSupplierInfo(undefined)
        setCenterSupplierInfo?.(undefined)
      }
    } else {
      setOrgSupplierId(undefined)
      setSupplierInfo(undefined)
      setCenterSupplierInfo?.(undefined)
    }
  }, [setOrgSupplierId, setSupplierInfo, setCenterSupplierInfo, setOrgSupplierIdList, setSupplierInfoList, setCenterSupplierInfoList, supplierMapByOrgId, findCenterSupplier, findCenterSuppliers])

  const getOptionLabel = useCallback((option: AutocompleteSupplier) => {
    const name = option.label ?? ''
    const id = option.orgSupplierId ?? ''
    return id ? `[${id}]${name}` : name
  }, [])

  const getSelectedOrgSupplierIds = useCallback(
    (selected: AutocompleteSupplier[]) =>
      selected
        .map((v) => v.orgSupplierId)
        .filter((id): id is string => id != null && id !== ''),
    []
  )

  // Check if info mark should be displayed
  const shouldShowInfoMark = useMemo(() => {
    if (hideInfoMark) return false
    if (!isMultiple) {
      return true
    }
    return false
  }, [isMultiple, hideInfoMark])

  const handleAutocompleteChange = useCallback((e: SyntheticEvent<Element, Event>, selectedValue: any) => {
    handleSingleSelection(selectedValue as AutocompleteSupplier | null)
  }, [handleSingleSelection])

  const handleSupplierOptionEqualToValue = useCallback((option: AutocompleteSupplier, value: AutocompleteSupplier) =>
    option.orgSupplierId === value.orgSupplierId
    , [])

  const handleSupplierRenderInput = useCallback((params: any) => {
    const hasError = isRequred && !trValue
    return (
      <TextField
        label="仕入先"
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...params}
        className={clsx('select-supplier-text-field')}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {supplierSearchIsLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : null}
              {params.InputProps.endAdornment}
            </>
          ),
          // readOnly: true,
          // tabIndex: -1,
        }}
        error={hasError}
        required={isRequred}
      />
    )
  }, [supplierSearchIsLoading, isRequred, trValue])

  return (
    <>
      <SelectFormControl isMultiple={isMultiple}>
        {isMultiple ? (
          <Autocomplete
            multiple
            disableCloseOnSelect
            limitTags={1}
            id={`supplier-select-${uniqueId}`}
            sx={{ width: '100%' }}
            value={trValueList}
            options={filteredSuppliers}
            loading={supplierSearchIsLoading}
            disabled={supplierSearchIsLoading}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={(option, value) => option.orgSupplierId === value.orgSupplierId}
            onOpen={() => {
              onMultipleDropdownOpen?.(getSelectedOrgSupplierIds(latestTrValueListRef.current))
            }}
            onClose={() => {
              onMultipleDropdownClose?.(getSelectedOrgSupplierIds(latestTrValueListRef.current))
            }}
            onChange={(_e, selectedValues, reason) => {
              handleMultipleSelection(selectedValues)
              if (reason === 'removeOption' || reason === 'clear') {
                onMultipleChipRemoved?.()
              }
            }}
            renderOption={(props, option, { selected }) => {
              // eslint-disable-next-line react/prop-types
              const { key, ...optionProps } = props
              const SelectionIcon = selected ? CheckBoxIcon : CheckBoxOutlineBlankIcon
              return (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <li key={key} {...optionProps}>
                  <SelectionIcon
                    fontSize="small"
                    style={{ marginRight: 8, padding: 9, boxSizing: 'content-box' }}
                  />
                  {getOptionLabel(option)}
                </li>
              )
            }}
            renderInput={(params) => {
              const hasError = isRequred && trValueList.length === 0
              return (
                <TextField
                  label="仕入先"
                  placeholder="仕入先"
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  className={clsx('select-supplier-text-field')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {supplierSearchIsLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  error={hasError}
                  required={isRequred}
                />
              )
            }}
            slotProps={{
            popper: { placement: startSelectTop ? 'top-start' : 'bottom-start' },
            }}
          />
        ) : (
          <Autocomplete
            disablePortal
            id="supplier"
            value={trValue ?? null}
            inputValue={trInputValue}
            options={filteredSuppliers}
            getOptionLabel={getOptionLabel}
            loading={supplierSearchIsLoading}
            disabled={supplierSearchIsLoading}
            freeSolo={false}
            selectOnFocus={false}
            clearOnBlur={false}
            handleHomeEndKeys
            onChange={handleAutocompleteChange}
            onInputChange={(_e, value, reason) => {
              // Keep input text in sync; clear when programmatically reset
              if (reason === 'reset') return
              setTrInputValue(value)
            }}
            isOptionEqualToValue={handleSupplierOptionEqualToValue}
            renderInput={handleSupplierRenderInput}
            slotProps={{
              popper: { placement: startSelectTop ? 'top-start' : 'bottom-start' },
            }}
          />
        )}
      </SelectFormControl>
      {shouldShowInfoMark && (
        <Grid
          sx={{
            display: 'flex',
            alignItems: alignmentFlag ? 'flex-end' : 'center',
            mr: 1,
            cursor: 'pointer',
          }}
          onClick={() => setOpen(true)}
        >
          <ErrorIcon sx={{ fontSize: 35 }} />
        </Grid>
      )}
      <SupplierDetail
        supplier={_.find(supplierSearch?.list, { orgSupplierId })}
        centerSupplier={findCenterSupplier(orgSupplierId)}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  )
}

export default SelectSupplier